import { useEffect, useState, useRef } from 'react'
import WizrInput from '../WizrInput'
import WizrTextArea from '../WizrTextArea'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrFlexLayout from '../WizrFlexLayout'
import { styled } from 'styled-components'
import WizrToggleButton from '../WizrToggleButton'
import WizrTagInput from '../WizrTagInput'
import WizrText from '../WizrText'
import { useContentLoadingSelector, useTagsSelector } from '../../redux/selectors/content'
import { dispatchTags } from '../../redux/actions/content'
import RadioGroup from '../WizrRadiogroup'
import WizrDropDown from '../WizrDropDown'
import { useAuth } from '../../authContext'
import WizrLoader from '../WizrLoader'

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
  width: 100%;
  position: relative;
`

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
  width: 100%;
`

const TagInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

interface ICreateAppForm {
  appData?: IAppSettings;
  onFormSubmission: (params: { [key: string]: any }) => void;
  onCancel: () => void;
}

interface IAppSettings {
  appName: string;
  memory: boolean;
  appDescription: string;
  appTags: string[];
  app_category: 'general' | 'package';
  app_feature: string;
  app_industry: string;
}

const CreateAppForm = ({ appData, onFormSubmission, onCancel }: ICreateAppForm): JSX.Element => {
  const { hasAcces } = useAuth();
  const loading = useContentLoadingSelector()
  const [isTagsLoading, setIsTagsLoading] = useState(true)

  useEffect(() => {
    dispatchTags()
  }, [])
  
  const tags = useTagsSelector()
  const tagList = tags?.map((item: any) => ({
    label: item,
    value: item,
  }))

  useEffect(() => {
    if (tags !== undefined) {
      setIsTagsLoading(false)
    } else if (loading) {
      setIsTagsLoading(true)
    }
  }, [tags, loading])

  // Form state
  const [appSettingState, setAppSettingState] = useState<IAppSettings>(appData ?? {
    appName: '',
    memory: false,
    appDescription: '',
    appTags: [],
    app_category: 'general',
    app_feature: '',
    app_industry: ''
  });

  // Validation state
  const [showAppNameValidation, setShowAppNameValidation] = useState(false)
  const [showAppDescriptionValidation, setShowAppDescriptionValidation] = useState(false)

  // Refs for error scrolling
  const appNameInputRef = useRef<HTMLDivElement>(null)
  const appDescriptionInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (appData) {
      setAppSettingState({
        appName: appData?.appName || '',
        memory: appData?.memory || false,
        appDescription: appData?.appDescription || '',
        appTags: appData?.appTags || [],
        app_category: appData?.app_category || 'general',
        app_feature: appData?.app_feature || '',
        app_industry: appData?.app_industry || ''
      })
    }
  }, [appData])

  const doUpdateFields = (fieldName: keyof IAppSettings, fieldValue: any) => {
    setAppSettingState(prev => ({
      ...prev,
      [fieldName]: fieldValue
    }))
  }

  const validateAllFields = () => {
    const isBlank = (text?: string) => !text || text.trim() === ''
    let isValid = true
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null

    // Reset validation states
    setShowAppNameValidation(false)
    setShowAppDescriptionValidation(false)

    // Validate app name
    if (isBlank(appSettingState.appName)) {
      setShowAppNameValidation(true)
      if (!firstErrorRef) firstErrorRef = appNameInputRef
      isValid = false
    }

    // Validate app description
    if (isBlank(appSettingState.appDescription)) {
      setShowAppDescriptionValidation(true)
      if (!firstErrorRef) firstErrorRef = appDescriptionInputRef
      isValid = false
    }

    // Scroll to first error
    if (!isValid && firstErrorRef?.current) {
      setTimeout(() => {
        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    return isValid
  }

  const handleSubmit = () => {
    if (!validateAllFields()) {
      return
    }

    onFormSubmission({
      appName: appSettingState.appName,
      memory: appSettingState.memory,
      appDescription: appSettingState.appDescription,
      appTags: appSettingState.appTags,
      app_category: appSettingState.app_category,
      app_feature: appSettingState.app_feature,
      app_industry: appSettingState.app_industry
    })
  }

  return (
    <StyledContainer background='#FFF' justifyContent='space-evenly'>
      <div style={{ width: '100%', position: 'relative' }}>
        {loading && (
          <WizrLoader
            loading={true}
            fullPage={false}
            overlay={true}
          />
        )}
        <WizrFlexLayout
          flexDirection='column'
          justifyContent='flex-start'
          background='#fff'
          alignItems='flex-start'
          style={{ width: '100%', gap: '16px' }}
        >
          <InputContainer ref={appNameInputRef}>
            <WizrText type='body2'>App Name</WizrText>
            <WizrInput
              style={{ width: '100%' }}
              background='#F2F4F9'
              placeholder='Enter app name'
              border='1px solid #000'
              value={appSettingState.appName}
              onChange={(value: string) => {
                doUpdateFields('appName', value)
                if (value && showAppNameValidation) {
                  setShowAppNameValidation(false)
                }
              }}
              showError={showAppNameValidation}
              errorMessage={showAppNameValidation ? "Please Enter an App Name" : undefined}
              disabled={loading}
            />
          </InputContainer>

          <InputContainer>
            <WizrText type='body2'>Memory</WizrText>
            <WizrFlexLayout
              flexDirection='row'
              alignContent='space-between'
              background='white'
              alignItems='baseline'
            >
              <WizrToggleButton
                isEnabled={appSettingState.memory}
                onChange={(e: boolean) => {
                  doUpdateFields('memory', e)
                }}
                disabled={loading}
              />
            </WizrFlexLayout>
          </InputContainer>

          <InputContainer>
            <WizrText type='body2'>Tags</WizrText>
            <TagInputWrapper>
              {!isTagsLoading && (
                <WizrTagInput
                  background='#F2F4F9'
                  options={tagList}
                  value={appSettingState?.appTags}
                  onChange={(value: any) => {
                    doUpdateFields('appTags', value)
                  }}
                  placeHolder='Select tags'
                  style={{ height: '52px', width: '100%' }}
                  maxTagCount={'responsive'}
                />
              )}
              {isTagsLoading && (
                <WizrLoader
                  loading={true}
                  fullPage={false}
                  overlay={false}
                />
              )}
            </TagInputWrapper>
          </InputContainer>

          <InputContainer>
            <WizrText type='body2'>App Type</WizrText>
            <RadioGroup
              options={[
                { label: 'General', value: 'general' },
                { label: 'Template', value: 'package' }
              ]}
              value={appSettingState.app_category}
              onChange={(e: { target: { value: 'general' | 'package' } }) => {
                doUpdateFields('app_category', e.target.value)
              }}
              disabled={!hasAcces('create_packages') || loading}
            />
          </InputContainer>

          {appSettingState?.app_category === 'package' ? (
            <>
              <InputContainer>
                <WizrText type='body2'>Link to CX Feature</WizrText>
                <WizrDropDown
                  options={[
                    hasAcces('view_autotag') &&
                    { label: 'Autotag', value: 'autotag' },
                    hasAcces('view_autosolve') &&
                    { label: 'AI Agent', value: 'autosolve' },
                    hasAcces('view_agentassist') &&
                    { label: 'Agent Assist', value: 'agentassist' },
                    hasAcces('view_agenttag') &&
                    { label: 'Agent Tag', value: 'agenttag' },
                  ].filter(Boolean)}
                  value={appSettingState?.app_feature}
                  placeHolder='Select feature'
                  onChange={(value: string) => {
                    doUpdateFields('app_feature', value)
                  }}
                  disabled={loading}
                />
              </InputContainer>

              <InputContainer>
                <WizrText type='body2'>Industry</WizrText>
                <WizrDropDown
                  options={[
                    { label: 'E-commerce', value: 'E-commerce' },
                    { label: 'SaaS', value: 'SaaS' },
                    { label: 'others', value: 'others' }
                  ]}
                  value={appSettingState?.app_industry}
                  placeHolder='Select industry'
                  onChange={(value: string) => {
                    doUpdateFields('app_industry', value)
                  }}
                  disabled={loading}
                />
              </InputContainer>
            </>
          ) : (
            <>
            </>
          )}

          <InputContainer ref={appDescriptionInputRef}>
            <WizrText type='body2'>App Description</WizrText>
            <WizrTextArea
              background='#F2F4F9'
              value={appSettingState.appDescription}
              border='1px solid var(--grayscale-300, #DCDEE2)'
              placeholder='Enter description'
              minRows={5}
              onChange={(value: string) => {
                doUpdateFields('appDescription', value)
                if (value && showAppDescriptionValidation) {
                  setShowAppDescriptionValidation(false)
                }
              }}
              showError={showAppDescriptionValidation}
              errorMessage={showAppDescriptionValidation ? "Please Enter an App Description" : undefined}
              disabled={loading}
            />
          </InputContainer>

          <WizrFlexSpacing
            justifyContent='flex-end'
            flexDirection='row'
            size='large'
            background='#FFF'
          >
            <WizrButton type='default' text='Cancel' onClick={onCancel} style={{ borderRadius: '20px' }} disabled={loading} />
            <WizrButton text='Save' onClick={handleSubmit} style={{ borderRadius: '20px' }} disabled={loading} />
          </WizrFlexSpacing>
        </WizrFlexLayout>
      </div>
    </StyledContainer>
  )
}

export default CreateAppForm